<script setup lang="ts">
const store = usePrestacaoDeContaStore()
const usuarioStore = useUsuarioStore()
const { modalDetalhesDespesa } = storeToRefs(store)

const despesa = computed(() => store.despesas?.find(d => d.id === modalDetalhesDespesa.value.despesa_id))
const abaAtiva = ref('detalhes')

// Função para trocar cartório
async function trocarCartorio () {
  try {
    if (modalDetalhesDespesa.value.cartorioInfo?.id) {
      await usuarioStore.selecionarCartorio(modalDetalhesDespesa.value.cartorioInfo.id, true)
      // Modal permanece aberto com manterContexto = true

      // Atualizar flags para ocultar o alerta
      modalDetalhesDespesa.value.cartorioDiferente = false
      modalDetalhesDespesa.value.cartorioInfo = null
    }
  } catch (error) {
    console.error('Erro ao trocar cartório:', error)
    useStore().toasts.push({
      content: 'Erro ao trocar de cartório',
      color: 'danger',
    })
  }
}

function fechar () {
  store.resetarModalDetalhesDespesa()
}

function contaPorId (id) {
  return store.contas.find(c => c.id === id)?.nome
}

async function downloadComprovantes (despesa) {
  try {
    await store.downloadComprovantes(despesa)
  } catch (error) {
    console.error('Erro ao baixar comprovantes:', error)
  }
}

function obterComprovanteUnificado () {
  const comprovantes = despesa?.value?.comprovantes
  // sort by created_at
  const comprovantesUnificados = comprovantes?.filter(comprovante => comprovante.arquivo_tipo === 'COMPROVANTE_UNIFICADO')?.sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at)
  })
  if (comprovantesUnificados?.length) {
    return comprovantesUnificados[0]
  }
}

async function criarNovoComprovanteUnificado () {
  try {
    const comprovante = obterComprovanteUnificado()
    await store.excluirComprovante(despesa.value.id, comprovante.id)
    await store.downloadComprovantes(despesa.value)
  } catch (error) {
    console.error('Erro ao criar comprovante unificado:', error)
  }
}

const despesasRecorrentes = ref<any[]>([])
const logs = ref<any[]>([])
const carregandoLogs = ref(false)
const logsExpandidos = ref<Record<number, boolean>>({})

async function buscarDespesasRecorrentes () {
  if (despesa.value?.recorrencia_id) {
    try {
      despesasRecorrentes.value = await store.buscarDespesasRecorrentes(despesa.value.recorrencia_id)
    } catch (error) {
      console.error('Erro ao buscar despesas recorrentes:', error)
    }
  }
}

async function buscarLogs () {
  if (despesa.value?.id) {
    try {
      carregandoLogs.value = true
      logs.value = await store.buscarLogsDespesa(despesa.value.id)
    } catch (error) {
      console.error('Erro ao buscar logs da despesa:', error)
      logs.value = []
    } finally {
      carregandoLogs.value = false
    }
  }
}

function formatarDataHora (dataHora: string) {
  return new Date(dataHora).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function obterNomeUsuario (log: any) {
  return log.usuario?.pessoa?.especializacao?.nome || log.usuario?.nome || 'Usuário desconhecido'
}

function obterDescricaoAcao (log: any) {
  const acoes = {
    CRIAR: 'Criou a despesa',
    EDITAR: 'Editou a despesa',
    EXCLUIR: 'Excluiu a despesa',
    ANEXAR_ARQUIVO: `Anexou o arquivo "${log.metadados?.nome_arquivo || 'arquivo'}"`,
    REMOVER_ARQUIVO: `Removeu o arquivo "${log.metadados?.nome_arquivo || 'arquivo'}"`,
  }
  return acoes[log.acao as keyof typeof acoes] || log.acao
}

function alternarExpandirLog (logId: number) {
  logsExpandidos.value[logId] = !logsExpandidos.value[logId]
}

function obterAlteracoes (log: any) {
  if (!log.dados_anteriores || !log.dados_novos) {
    return []
  }

  const alteracoes = []
  const campos = {
    descricao: 'Descrição',
    valor: 'Valor',
    vencimento: 'Vencimento',
    data_pagamento: 'Data de Pagamento',
    observacao: 'Observação',
    conta_id: 'Conta',
    despesa_efetiva: 'Despesa Direta',
  }

  for (const campo in campos) {
    let valorAnterior = log.dados_anteriores[campo]
    let valorNovo = log.dados_novos[campo]

    // Comparação especial para valores monetários - ignora diferenças apenas de tipo
    if (campo === 'valor') {
      const valorAnteriorNumerico = Number(valorAnterior)
      const valorNovoNumerico = Number(valorNovo)

      // Se os valores numéricos são iguais, não considera como alteração
      if (valorAnteriorNumerico === valorNovoNumerico) {
        continue
      }
    }

    // Para outros campos, comparação normal
    if (valorAnterior !== valorNovo) {
      // Formatação especial para alguns campos
      if (campo === 'valor') {
        valorAnterior = `R$ ${Number(valorAnterior).toFixed(2).replace('.', ',')}`
        valorNovo = `R$ ${Number(valorNovo).toFixed(2).replace('.', ',')}`
      } else if (campo === 'vencimento' || campo === 'data_pagamento') {
        valorAnterior = valorAnterior ? new Date(valorAnterior).toLocaleDateString('pt-BR') : 'Não definido'
        valorNovo = valorNovo ? new Date(valorNovo).toLocaleDateString('pt-BR') : 'Não definido'
      } else if (campo === 'conta_id') {
        valorAnterior = contaPorId(valorAnterior) || valorAnterior
        valorNovo = contaPorId(valorNovo) || valorNovo
      } else if (campo === 'despesa_efetiva') {
        valorAnterior = valorAnterior ? 'Sim' : 'Não'
        valorNovo = valorNovo ? 'Sim' : 'Não'
      }

      alteracoes.push({
        campo: campos[campo as keyof typeof campos],
        anterior: valorAnterior || 'Vazio',
        novo: valorNovo || 'Vazio',
      })
    }
  }

  return alteracoes
}

watch(() => modalDetalhesDespesa.value.exibir, (novoValor) => {
  if (novoValor) {
    buscarDespesasRecorrentes()
    buscarLogs()
  }
})
</script>

<template>
  <!-- Modal para exibir detalhes da despesa -->
  <CModal
    id="modalLancarDespesa"
    :visible="modalDetalhesDespesa.exibir"
    size="lg"
    @close="fechar"
  >
    <CModalHeader>
      <CModalTitle id="lancarDespesa">
        Detalhes Despesa
      </CModalTitle>
    </CModalHeader>
    <CModalBody>
      <!-- Alerta de cartório diferente -->
      <CAlert
        v-if="modalDetalhesDespesa.cartorioDiferente && modalDetalhesDespesa.cartorioInfo"
        color="info"
        class="mb-3"
      >
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <CIcon icon="cil-info" class="me-2" />
            Esta despesa pertence ao cartório {{ usuarioStore.formatarNomeCartorio(modalDetalhesDespesa.cartorioInfo) }}, diferente do cartório atualmente selecionado.
          </div>
          <CButton
            color="primary"
            size="sm"
            @click="trocarCartorio"
          >
            Trocar para este cartório
          </CButton>
        </div>
      </CAlert>

      <!-- Abas -->
      <CTabs v-model="abaAtiva" class="mb-4">
        <CNav variant="tabs">
          <CNavItem>
            <CNavLink href="#" :active="abaAtiva === 'detalhes'" @click="abaAtiva = 'detalhes'">
              Detalhes
            </CNavLink>
          </CNavItem>
          <CNavItem v-if="despesa?.recorrencia_id">
            <CNavLink href="#" :active="abaAtiva === 'recorrencia'" @click="abaAtiva = 'recorrencia'">
              Recorrência
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink href="#" :active="abaAtiva === 'historico'" @click="abaAtiva = 'historico'">
              Histórico/Ocorrências
            </CNavLink>
          </CNavItem>
        </CNav>

        <CTabContent class="mt-3">
          <!-- Aba de Detalhes -->
          <CTabPane :visible="abaAtiva === 'detalhes'">
            <div v-if="despesa?.despesa_efetiva === false" class="alert alert-warning">
              <CIcon icon="cil-info" class="me-2" />
              Esta despesa foi lançada como "Despesa <strong>INDIRETA</strong>".
            </div>
            <div>
              <label>Descrição:</label>
              <p class="text-break text-wrap overflow-auto" style="max-height: 350px; overflow-y: auto;" v-html="despesa?.descricao" />
            </div>
            <div>
              <label>Conta:</label>
              <p>{{ contaPorId(despesa?.conta_id) }}</p>
            </div>
            <div>
              <label>Valor:</label>
              <p>{{ useStore().formatarDinheiro(despesa?.valor, true) }}</p>
            </div>
            <div>
              <label>Vencimento:</label>
              <p>{{ useStore().formatarData(despesa?.vencimento) }}</p>
            </div>
            <div>
              <label>Data de pagamento:</label>
              <p v-if="despesa?.data_pagamento">
                {{ useStore().formatarData(despesa?.data_pagamento) }}
              </p>
              <p v-else>
                Pendente
              </p>
            </div>
            <!-- <div>
              <label>Observações:</label>
              <p>{{ despesa.observacoes }}</p>
            </div> -->
            <div v-if="despesa?.numero_parcela && despesa?.total_parcelas" class="mb-3">
              <label>Parcela:</label>
              <p class="text-primary fw-bold">
                <CIcon icon="cil-layers" class="me-2" />
                Parcela {{ despesa.numero_parcela }}/{{ despesa.total_parcelas }}
              </p>
            </div>
            <div v-if="despesa?.eventoCalendario" class="mb-3">
              <label>Evento no Calendário:</label>
              <p>
                <CIcon icon="cil-calendar-check" class="text-primary me-2" />
                Evento agendado para {{ useStore().formatarData(despesa?.eventoCalendario?.start_time) }}
              </p>
            </div>
            <div v-if="despesa" class="comprovantes-section">
              <label>Comprovantes:</label>
              <div v-if="despesa.comprovantes?.length">
                <template v-for="comprovante in despesa.comprovantes" :key="comprovante.id">
                  <div v-if="!['ARQUIVOS_MESCLADOS', 'COMPROVANTE_UNIFICADO'].includes(comprovante.arquivo_tipo)" class="comprovante-item" @click="store.downloadComprovante(despesa.id, comprovante.id)">
                    <CIcon name="cil-cloud-download" />
                    <span>{{ comprovante.arquivo_nome }}</span>
                  </div>
                </template>
                <CButton v-if="!obterComprovanteUnificado()" color="primary" size="sm" class="mt-2" @click="downloadComprovantes(despesa)">
                  <i class="bi bi-download me-1" />
                  Baixar todos os comprovantes
                </CButton>
              </div>
              <p v-else>
                Nenhum comprovante anexado
              </p>
            </div>
            <div v-if="obterComprovanteUnificado() != null" class="comprovante_unificado mt-4">
              <label>Comprovante Unificado:</label><br>
              <CButton
                v-c-tooltip="'Baixar todos os comprovantes em um arquivo único.'"
                color="primary"
                size="sm"
                class="mt-2 me-3"
                @click="downloadComprovantes(despesa)"
              >
                <CIcon name="cil-cloud-download" />
                Baixar comprovante unificado
              </CButton>
              <CButton v-c-tooltip="'Essa ação apaga o comprovante unificado atual e gera um novo.'" color="warning" size="sm" class="mt-2" @click="criarNovoComprovanteUnificado">
                <CIcon name="cil-reload" />
                Criar novo comprovante unificado
              </CButton>
            </div>
          </CTabPane>

          <!-- Aba de Recorrência -->
          <CTabPane :visible="abaAtiva === 'recorrencia'">
            <div v-if="modalDetalhesDespesa.despesasRecorrentes.length > 0">
              <table class="table">
                <thead>
                  <tr>
                    <th style="width: 40px" />
                    <th>Parcela</th>
                    <th>Vencimento</th>
                    <th>Data de Pagamento</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="despRec in modalDetalhesDespesa.despesasRecorrentes" :key="despRec.id">
                    <tr>
                      <td>
                        <CButton
                          v-if="despRec.comprovantes?.length"
                          color="link"
                          class="p-0"
                          @click="despRec.showComprovantes = !despRec.showComprovantes"
                        >
                          <CIcon
                            :icon="despRec.showComprovantes ? 'cil-chevron-bottom' : 'cil-chevron-right'"
                            size="sm"
                          />
                        </CButton>
                      </td>
                      <td>
                        <span v-if="despRec.numero_parcela && despRec.total_parcelas" class="text-muted small">
                          {{ despRec.numero_parcela }}/{{ despRec.total_parcelas }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>{{ useStore().formatarData(despRec.vencimento) }}</td>
                      <td>{{ despRec.data_pagamento ? useStore().formatarData(despRec.data_pagamento) : '-' }}</td>
                      <td>
                        <CBadge :color="despRec.data_pagamento ? 'success' : 'warning'">
                          {{ despRec.data_pagamento ? 'Pago' : 'Pendente' }}
                        </CBadge>
                      </td>
                    </tr>
                    <tr v-if="despRec.showComprovantes && despRec.comprovantes?.length">
                      <td colspan="5" class="border-0 pt-0">
                        <div class="comprovantes-expandidos">
                          <template v-for="comprovante in despRec.comprovantes" :key="comprovante.id">
                            <div
                              v-if="!['ARQUIVOS_MESCLADOS', 'COMPROVANTE_UNIFICADO'].includes(comprovante.arquivo_tipo)"
                              class="comprovante-item px-4"
                              @click="store.downloadComprovante(despRec.id, comprovante.id)"
                            >
                              <CIcon name="cil-cloud-download" />
                              <span>{{ comprovante.arquivo_nome }}</span>
                            </div>
                          </template>
                          <CButton
                            v-if="despRec.comprovantes.length > 1"
                            color="primary"
                            size="sm"
                            class="ms-4 mt-2"
                            variant="ghost"
                            @click="downloadComprovantes(despRec)"
                          >
                            <CIcon name="cil-cloud-download" class="me-1" />
                            Baixar todos
                          </CButton>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center py-3">
              <p class="text-muted">
                Nenhuma despesa recorrente encontrada
              </p>
            </div>
          </CTabPane>

          <!-- Aba de Histórico/Ocorrências -->
          <CTabPane :visible="abaAtiva === 'historico'">
            <div v-if="carregandoLogs" class="text-center py-3">
              <CSpinner size="sm" />
              <span class="ms-2">Carregando histórico...</span>
            </div>
            <div v-else-if="logs.length > 0">
              <div class="timeline">
                <div v-for="log in logs" :key="log.id" class="timeline-item">
                  <div class="timeline-marker">
                    <CIcon :name="log.acao === 'CRIAR' ? 'cil-plus' : log.acao === 'EDITAR' ? 'cil-pencil' : log.acao === 'EXCLUIR' ? 'cil-trash' : log.acao === 'ANEXAR_ARQUIVO' ? 'cil-paperclip' : 'cil-minus'" />
                  </div>
                  <div class="timeline-content">
                    <div
                      :class="log.acao === 'EDITAR' && obterAlteracoes(log).length > 0 ? 'timeline-header-clickable' : 'timeline-header-non-clickable'"
                      @click="log.acao === 'EDITAR' && obterAlteracoes(log).length > 0 ? alternarExpandirLog(log.id) : null"
                    >
                      <div class="d-flex align-items-center justify-content-between">
                        <div class="timeline-summary">
                          <strong>{{ obterDescricaoAcao(log) }}</strong> por <strong>{{ obterNomeUsuario(log) }}</strong>, em {{ formatarDataHora(log.created_at) }}
                        </div>
                        <CIcon
                          v-if="log.acao === 'EDITAR' && obterAlteracoes(log).length > 0"
                          :name="logsExpandidos[log.id] ? 'cil-chevron-bottom' : 'cil-chevron-right'"
                          class="chevron-icon"
                        />
                      </div>
                    </div>
                    <div v-if="logsExpandidos[log.id] && log.acao === 'EDITAR' && obterAlteracoes(log).length > 0" class="timeline-changes mt-2">
                      <small class="text-muted">Alterações:</small>
                      <div v-for="alteracao in obterAlteracoes(log)" :key="alteracao.campo" class="change-item">
                        <strong>{{ alteracao.campo }}:</strong>
                        <div class="change-values">
                          <span class="old-value" v-html="alteracao.anterior" />
                          <CIcon name="cil-arrow-right" class="mx-2" />
                          <span class="new-value" v-html="alteracao.novo" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-3">
              <p class="text-muted">
                Nenhum registro de alteração encontrado
              </p>
            </div>
          </CTabPane>
        </CTabContent>
      </CTabs>
    </CModalBody>
    <CModalFooter>
      <CButton color="secondary" @click="fechar">
        Fechar
      </CButton>
    </CModalFooter>
  </CModal>
</template>

<style scoped>
label{ font-weight: bold; }
.comprovantes-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.comprovante-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  cursor: pointer;
}
.comprovante-item:hover {
  background-color: #eee;
}

.comprovante-item i {
  color: #6c757d;
}

.comprovantes-expandidos {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
}

.comprovante-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.comprovante-item:hover {
  background-color: #e9ecef;
}

tr + tr.border-0 {
  border-top: none !important;
}

/* Estilos para o histórico de alterações */
.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 1rem;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #dee2e6;
}

.timeline-item {
  position: relative;
  margin-bottom: 1.5rem;
}

.timeline-marker {
  position: absolute;
  left: -2rem;
  top: 0.25rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: #6c757d;
}

.timeline-content {
  background: #f8f9fa;
  border-radius: 0.375rem;
  padding: 1rem;
  border-left: 3px solid #0d6efd;
}

.timeline-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.timeline-action {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.timeline-changes {
  background: #fff;
  border-radius: 0.25rem;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
}

.change-item {
  margin-bottom: 0.5rem;
}

.change-item:last-child {
  margin-bottom: 0;
}

.change-values {
  display: flex;
  align-items: center;
  margin-top: 0.25rem;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

.old-value {
  color: #dc3545;
  background: #f8d7da;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  text-decoration: line-through;
}

.new-value {
  color: #155724;
  background: #d4edda;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

/* Estilos para o histórico colapsável */
.timeline-header-clickable {
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 0.5rem;
  border-radius: 0.25rem;
}

.timeline-header-clickable:hover {
  background-color: #f1f3f4;
}

.timeline-header-non-clickable {
  padding: 0.5rem;
  border-radius: 0.25rem;
}

.timeline-summary {
  flex: 1;
  font-size: 0.875rem;
  line-height: 1.4;
}

.chevron-icon {
  font-size: 0.875rem;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.timeline-content {
  padding: 0.75rem;
}
</style>
